import { forwardRef, type SelectHTMLAttributes, useState } from 'react';
import { cn } from '../../utils/cn';
import { borderRadius } from '../../tokens/spacing';
import { transitions } from '../../utils/motion';

type SelectSize = 'sm' | 'md' | 'lg';

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helperText?: string;
  selectSize?: SelectSize;
  placeholder?: string;
}

const chevronDownSvg = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23616161' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`;

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      error,
      helperText,
      selectSize = 'md',
      placeholder,
      children,
      id,
      disabled,
      value,
      className,
      ...props
    },
    ref,
  ): JSX.Element => {
    const selectId = id ?? (label ? `select-${label.replace(/\s+/g, '-').toLowerCase()}` : undefined);
    const errorId = selectId ? `${selectId}-error` : undefined;
    const helperId = selectId ? `${selectId}-helper` : undefined;
    const [focused, setFocused] = useState(false);

    const sizeStyles: Record<SelectSize, { padding: string; fontSize: string; height: string }> = {
      sm: { padding: '0.375rem 2rem 0.375rem 0.625rem', fontSize: '0.8125rem', height: '32px' },
      md: { padding: '0.5rem 2rem 0.5rem 0.75rem', fontSize: '0.875rem', height: '40px' },
      lg: { padding: '0.625rem 2rem 0.625rem 0.875rem', fontSize: '1rem', height: '48px' },
    };

    const currentSize = sizeStyles[selectSize];

    return (
      <div
        className={cn('ds-select-wrapper', className)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.25rem',
          width: '100%',
          /* `flex` e `minWidth` para o campo se comportar nos DOIS contextos:
             em pai block, `width: 100%` manda e o campo preenche a linha; em
             pai flex (toolbar de filtro), `flex-basis: 0` deixa os campos
             dividirem a linha em vez de cada um reivindicar 100% e quebrar
             para a propria linha.

             Sem isso, a toolbar de /fiscal/invoices — que ja era `display:
             flex` — renderizava os tres filtros EMPILHADOS, cada um com a
             largura inteira do card. Visto na captura. */
          flex: '1 1 0%',
          minWidth: 0,
        }}
      >
        {label && (
          <label
            htmlFor={selectId}
            className="ds-select__label"
            style={{
              /* label-md do prototipo: 12px JetBrains Mono, 0.02em, 500,
                 em on-surface-variant (medido em 168 labels de campo) */
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: '12px',
              lineHeight: '16px',
              letterSpacing: '0.02em',
              fontWeight: 500,
              color: disabled ? '#74777f' : '#43474e',
            }}
          >
            {label}
          </label>
        )}

        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <select
            ref={ref}
            id={selectId}
            disabled={disabled}
            aria-invalid={!!error}
            aria-describedby={
              [error ? errorId : null, !error && helperText ? helperId : null]
                .filter(Boolean)
                .join(' ') || undefined
            }
            value={value}
            className={cn('ds-select__field', error && 'ds-select__field--error')}
            style={{
              width: '100%',
              padding: currentSize.padding,
              fontSize: currentSize.fontSize,
              lineHeight: 1.5,
              color: disabled ? '#74777f' : value === '' && placeholder ? '#74777f' : '#191c1e',
              /* `backgroundColor`, nao o atalho `background`: o atalho reseta
                 `background-image`/`background-repeat`, e React so reaplica o
                 que mudou — o chevron passava a repetir no campo inteiro ao
                 sair de desabilitado. */
              backgroundColor: disabled ? '#f2f4f6' : '#f7f9fb',
              border: `1px solid ${error ? '#ba1a1a' : focused ? '#002045' : disabled ? '#eceef0' : '#c4c6cf'}`,
              borderRadius: borderRadius.sm,
              transition: transitions.fast,
              outline: 'none',
              boxSizing: 'border-box',
              fontFamily: 'inherit',
              cursor: disabled ? 'not-allowed' : 'pointer',
              appearance: 'none',
              backgroundImage: disabled ? 'none' : chevronDownSvg,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 0.75rem center',
              boxShadow: focused
                ? error
                  ? '0 0 0 1px #ba1a1a'
                  : '0 0 0 1px #002045'
                : 'none',
              minHeight: currentSize.height,
            }}
            onFocus={(e) => {
              setFocused(true);
              props.onFocus?.(e);
            }}
            onBlur={(e) => {
              setFocused(false);
              props.onBlur?.(e);
            }}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {children}
          </select>
        </div>

        {error && (
          <p id={errorId} className="ds-select__error" role="alert" style={{ margin: 0, fontSize: '0.75rem', color: '#ba1a1a' }}>
            {error}
          </p>
        )}

        {helperText && !error && (
          <p id={helperId} className="ds-select__helper" style={{ margin: 0, fontSize: '0.75rem', color: '#43474e' }}>
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

Select.displayName = 'Select';

